import { useEffect, useRef } from 'react';

const key = (gameId) => `pokemon:seen-version:${gameId}`;

/**
 * The game's version when you last had it open on this device, before this visit: where "what you missed" starts.
 * While the game is open, every version you see is remembered for next time. Browser storage can be missing or
 * blocked; then nothing is remembered and the replay starts at the latest move.
 *
 * @returns {?number}
 */
export default function useLastSeenVersion(gameId, version) {
  const before = useRef(undefined);
  if (before.current === undefined) {
    try {
      const stored = window.localStorage.getItem(key(gameId));
      before.current = stored === null ? null : Number(stored);
    } catch (blocked) {
      // Storage unavailable (private browsing, blocked site data): start at the latest move instead.
      if (!(blocked instanceof DOMException)) {
        throw blocked;
      }
      before.current = null;
    }
  }

  useEffect(() => {
    if (version === null || version === undefined) {
      return;
    }
    try {
      window.localStorage.setItem(key(gameId), String(version));
    } catch (blocked) {
      // Storage unavailable or full: nothing to remember, by design (see above).
      if (!(blocked instanceof DOMException)) {
        throw blocked;
      }
    }
  }, [gameId, version]);

  return before.current;
}
